import React from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold } from '../HandbookContent';

export default function ChapterVisualEditor() {
  return (
    <HChapter
      title="The Visual Editor"
      subtitle="The interactive canvas where you build and visualize your workflow as a diagram."
    >
      <HMetaphor
        icon={<PencilSquareIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of It as the Blueprint Table"
      >
        The Visual Editor is your drafting table. Instead of describing a workflow in words, you lay
        it out as a diagram — boxes for each status, arrows for each transition. You can drag things
        around, connect them, and see the entire workflow at a glance. It's like sketching a blueprint
        for your case process.
      </HMetaphor>

      <HSection title="Navigating the Canvas">
        <HParagraph>
          The canvas is the large area where your workflow diagram lives. You can interact with it in
          several ways:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-1">
          <li><HBold>Pan</HBold> — Click and drag on empty space to move the view around</li>
          <li><HBold>Zoom</HBold> — Use your mouse scroll wheel or trackpad pinch to zoom in and out</li>
          <li><HBold>Fit view</HBold> — The canvas automatically fits all nodes when first loaded</li>
          <li><HBold>Mini-map</HBold> — A small overview in the bottom-right corner shows your position in the full diagram</li>
          <li><HBold>Controls</HBold> — Zoom buttons in the bottom-left for precise zoom control</li>
        </ul>
      </HSection>

      <HSection title="The Toolbar">
        <img
          src="/handbook/toolbar-buttons.svg"
          alt="Editor toolbar showing buttons: + Status, Transition, Auto-Layout, Court Rules, AI Assistant, Import Template, Show Changes, Test, Save (disabled), and Publish (emerald green)"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <HParagraph>
          The toolbar above the canvas has all the tools you need:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li><HBold>+ Status</HBold> — Add a new status node to the canvas</li>
          <li><HBold>Transition</HBold> — Toggle connection mode to draw transition arrows</li>
          <li><HBold>Auto-Layout</HBold> — Automatically arrange nodes in a clean left-to-right flow</li>
          <li><HBold>Court Rules</HBold> — Open the court rules browser to link statutory deadlines</li>
          <li><HBold>AI Assistant</HBold> — Open the AI panel for playbook generation and gap detection</li>
          <li><HBold>Import Template</HBold> — Import a task template as a new playbook</li>
          <li><HBold>Show Changes</HBold> — Compare against a previous version (diff view)</li>
          <li><HBold>Test</HBold> — Run a simulation to verify workflow paths</li>
          <li><HBold>Save</HBold> — Save your current layout changes</li>
          <li><HBold>Publish</HBold> — Make this playbook live (only visible when eligible)</li>
        </ul>
      </HSection>

      <HSection title="Adding and Editing Statuses">
        <HStep number={1} title="Click '+ Status' in the toolbar">
          A new status node appears on the canvas with a default name.
        </HStep>
        <HStep number={2} title="Click the new node to select it">
          The property panel on the right side of the screen shows the status details.
        </HStep>
        <HStep number={3} title="Edit in the property panel">
          Change the name, set the category (To Do, In Progress, Done, or Blocked),
          and configure whether it's an initial or terminal status.
        </HStep>
        <HStep number={4} title="Drag to reposition">
          Click and drag the node to move it to a better position on the canvas. Use
          Auto-Layout for automatic arrangement.
        </HStep>
      </HSection>

      <HSection title="The Property Panel">
        <img
          src="/handbook/property-panel.svg"
          alt="Property panel sidebar showing fields for a selected status: Name (Under Review), Slug (under-review), Category dropdown (In Progress with blue dot), Initial/Terminal checkboxes, SLA Hours (48), and a Delete Status button"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <HParagraph>
          The right sidebar shows details for whatever is currently selected:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-1">
          <li>
            <HBold>Status selected</HBold> — Shows the status name, slug, category, and position. You can
            edit properties and delete the status from here.
          </li>
          <li>
            <HBold>Transition selected</HBold> — Shows the transition name, trigger type, and lists of
            guards, validators, and actions. Click any item to edit its configuration.
          </li>
          <li>
            <HBold>Nothing selected</HBold> — Shows a summary of the playbook with total counts.
          </li>
        </ul>
      </HSection>

      <HTip>
        Hit the <HBold>Delete</HBold> or <HBold>Backspace</HBold> key to remove the selected node
        or transition. Don't worry — if you make a mistake, just add it back. Nothing is saved until
        you click "Save."
      </HTip>
    </HChapter>
  );
}
